import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Palette, Loader2 } from "lucide-react";
import { http, errMsg } from "../../lib/api";

const Row = ({ k, i, busy, set }) => {
  const lift = k.look === "lift";
  return (
    <li data-testid={`look-kit-${i}`} className="flex items-center gap-3 border-t border-stone-100 py-1.5 text-sm">
      <Link to={`/kits/${k.id}`} className="min-w-0 flex-1 truncate text-[#2B2A26] hover:text-[#9A7B2C]">{k.destination} <span className="text-xs text-stone-400">· {k.collection === "founders" ? "Founder\u2019s" : "Researched"} · {k.edition} · {k.tier} · built {(k.created_at || "").slice(0, 16).replace("T", " ")}</span></Link>
      <span data-testid={`look-kit-status-${i}`} className={`text-xs ${lift ? "text-[#9A7B2C] font-bold" : "text-stone-500"}`}>{lift ? "New look" : "Classic"}</span>
      <button data-testid={`look-kit-toggle-${i}`} disabled={busy} onClick={() => set(k.id, lift ? "classic" : "lift")} className="text-xs font-bold text-[#9A7B2C] hover:underline disabled:opacity-50">
        {lift ? "Back to classic" : "Try new look"}
      </button>
    </li>
  );
};

export const LookPanel = () => {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => http.get("/looks").then((x) => setD(x.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const run = async (fn, msg) => {
    setBusy(true);
    try { const r = await fn(); toast.success(msg(r.data)); await load(); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const set = (id, look) => run(() => http.post(`/kits/${id}/look`, { look }), () => (look === "lift" ? "New look on — the PDF is rebuilding, open the kit to view it" : "Back to the classic look"));
  const all = (look) => run(() => http.post("/looks/apply-all", { look }), (x) => `${look === "lift" ? "New" : "Classic"} look set on ${x.kits} kit(s) and for every new kit`);
  const kits = d?.kits || [];
  if (!kits.length) return null;
  const lifted = kits.filter((k) => k.look === "lift").length;
  return (
    <section data-testid="look-panel" className="mb-6 rounded-lg border border-stone-200 bg-white p-4">
      <div className="flex flex-wrap items-center gap-3">
        <Palette size={18} className="text-[#9A7B2C]" />
        <b className="text-[#2B2A26]">Kit look</b>
        <span data-testid="look-summary" className="text-sm text-stone-500">Bolder chapter openers, highlight lines, icon info boxes, pull-quote tips and a coloured day timeline · {lifted} of {kits.length} on the new look</span>
        <div className="ml-auto flex gap-2">
          {busy && <Loader2 size={15} className="animate-spin text-[#9A7B2C]" />}
          <button data-testid="look-apply-all-btn" disabled={busy || lifted === kits.length} onClick={() => all("lift")} className="rounded-sm bg-[#C9A847] px-3 py-1.5 text-xs font-bold text-white hover:bg-[#B8973A] disabled:opacity-50">Use new look on all</button>
          {lifted > 0 && <button data-testid="look-revert-all-btn" disabled={busy} onClick={() => all("classic")} className="rounded-sm border border-stone-300 px-3 py-1.5 text-xs font-bold text-stone-700 hover:bg-stone-50 disabled:opacity-50">All back to classic</button>}
        </div>
      </div>
      <ul className="mt-2 max-h-56 overflow-y-auto">{kits.map((k, i) => <Row key={k.id} k={k} i={i} busy={busy} set={set} />)}</ul>
    </section>
  );
};
